// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useTranslation } from 'react-i18next';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { type SegmentMatrix as SegmentMatrixInterface } from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { ChartContainer } from '@shared/ui-kit/components';
import { useMemo } from 'react';
import { transformMatrixData } from './utils/transform-matrix-data';

interface SegmentMatrixProps {
  segmentMatrix: SegmentMatrixInterface;
}

export const SegmentMatrix = ({
  segmentMatrix,
}: SegmentMatrixProps): JSX.Element => {
  const { t } = useTranslation();
  const data = useMemo(() => transformMatrixData(segmentMatrix), [segmentMatrix]);

  const colors = useMemo(
    () =>
      ({
        type: 'sequential',
        interpolator: (t: number) => `rgba(16, 114, 186, ${(t + 0.1) / 1.1})`,
        minValue: 0,
        maxValue: 100,
      } as const),
    [],
  );

  return (
    <ChartContainer
      header={t('common.segment-matrix', { ns: 'common' })}
      popupName="charts.segmentMatrix"
      tooltip
    >
      <ResponsiveHeatMap
        data={data}
        margin={{ top: 60, right: 40, bottom: 40, left: 80 }}
        forceSquare={true}
        xInnerPadding={0.1}
        yInnerPadding={0.1}
        axisTop={{
          tickSize: 0,
          tickPadding: 30,
          tickRotation: 0,
          legend: '',
          legendOffset: 46,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 18,
          tickRotation: 0,
          legend: '',
          legendPosition: 'middle',
          legendOffset: -72,
        }}
        colors={colors}
        emptyColor="var(--mutedText)"
        borderRadius={8}
        opacity={0.8}
        activeOpacity={0.9}
        inactiveOpacity={0.5}
        labelTextColor="var(--titleFontColor)"
        theme={{
          labels: {
            text: { fontSize: 12, fontFamily: 'poppins' },
          },
          axis: {
            ticks: {
              text: {
                fontSize: '1rem',
                fontFamily: 'poppins',
                fill: 'var(--blueGrayMuted)',
                textTransform: 'uppercase',
              },
            },
          },
        }}
        animate={false}
      />
    </ChartContainer>
  );
};
